import { useState, useEffect, useRef } from 'react';
import { FIELD_OPTIONS_CHANGED_EVENT } from './fieldOptions';

// ── Substrate value helpers (exported for use in App.js, Stats.js, PlantDetail.js) ──

export function parseSubstrate(val) {
  if (!val) return null;
  try { const p = JSON.parse(val); if (Array.isArray(p)) return p; } catch (e) {}
  return val;
}

export function formatSubstrate(val) {
  const p = parseSubstrate(val);
  if (Array.isArray(p)) return p.map(c => `${c.pct}% ${c.name}`).join(' / ');
  return p || '';
}

export function substrateComponents(val) {
  const p = parseSubstrate(val);
  if (Array.isArray(p)) return p.map(c => c.name);
  return p ? [p] : [];
}

// ── useGrowingMethods hook ────────────────────────────────────────────────────

export function useGrowingMethods() {
  const [methods, setMethods] = useState([]);
  useEffect(() => {
    let active = true;
    const load = () => {
      fetch('/growing-methods')
        .then(r => r.json())
        .then(data => { if (active) setMethods(data); })
        .catch(() => {});
    };

    load();
    window.addEventListener(FIELD_OPTIONS_CHANGED_EVENT, load);
    return () => {
      active = false;
      window.removeEventListener(FIELD_OPTIONS_CHANGED_EVENT, load);
    };
  }, []);
  return methods;
}

// ── SubstratePicker ───────────────────────────────────────────────────────────
// Chip-based substrate picker. Tap one chip for a single substrate, tap two or
// more to enter mixture mode with percentage inputs.
// Pass `key={methodName}` at the call site to remount on method change.

export function SubstratePicker({ substrates, value, onChange, onValidityChange, label = 'Substrate', methodName = '' }) {
  // { substrateName: pctString } — '' for single selection (no pct needed)
  const [selection, setSelection] = useState(() => {
    const p = parseSubstrate(value);
    if (Array.isArray(p)) return Object.fromEntries(p.map(c => [c.name, String(c.pct)]));
    if (p) return { [p]: '' };
    return {};
  });

  const [recentMixtures, setRecentMixtures] = useState([]);
  const lastEmittedRef = useRef(value);
  const mountedRef = useRef(false);
  const clearedPctNamesRef = useRef(new Set());

  const selectedNames = Object.keys(selection);
  const isMixture = selectedNames.length >= 2;

  // Fetch recent mixtures when method is known
  useEffect(() => {
    if (!methodName) { setRecentMixtures([]); return; }
    fetch(`/recent-substrate-mixtures?method=${encodeURIComponent(methodName)}`)
      .then(r => r.json())
      .then(data => setRecentMixtures(data.results || []))
      .catch(() => setRecentMixtures([]));
  }, [methodName]);

  // Emit whenever selection changes, skipping initial mount
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (selectedNames.length === 0) {
      if (lastEmittedRef.current !== '') { lastEmittedRef.current = ''; onChange(''); }
      onValidityChange?.(true);
      return;
    }
    if (selectedNames.length === 1) {
      const name = selectedNames[0];
      if (name !== lastEmittedRef.current) { lastEmittedRef.current = name; onChange(name); }
      onValidityChange?.(true);
      return;
    }
    const components = selectedNames.map(n => ({ name: n, pct: parseInt(selection[n]) || 0 }));
    const total = components.reduce((s, c) => s + c.pct, 0);
    if (total === 100 && components.every(c => c.pct > 0)) {
      const json = JSON.stringify(components);
      if (json !== lastEmittedRef.current) { lastEmittedRef.current = json; onChange(json); }
      onValidityChange?.(true);
    } else {
      onValidityChange?.(false);
    }
  }, [selection]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChipClick = (name) => {
    setSelection(prev => {
      const next = { ...prev };
      clearedPctNamesRef.current.delete(name);
      if (name in next) { delete next[name]; } else { next[name] = ''; }
      return next;
    });
  };

  const handlePctChange = (name, rawValue) => {
    setSelection(prev => {
      if (rawValue === '' && prev[name] !== '') {
        clearedPctNamesRef.current.add(name);
      } else if (rawValue !== '') {
        clearedPctNamesRef.current.delete(name);
      }
      return { ...prev, [name]: rawValue };
    });
  };

  const handlePctBlur = (name) => {
    setSelection(prev => {
      const next = { ...prev };
      const emptyNames = Object.keys(next).filter(n => next[n] === '');
      if (emptyNames.length === 1 && !clearedPctNamesRef.current.has(emptyNames[0])) {
        const sum = Object.entries(next).reduce(
          (s, [n, v]) => n !== emptyNames[0] ? s + (parseInt(v) || 0) : s, 0
        );
        const rem = 100 - sum;
        if (rem > 0 && rem <= 99) {
          next[emptyNames[0]] = String(rem);
          clearedPctNamesRef.current.delete(emptyNames[0]);
        }
      }
      return next;
    });
  };

  const applyRecentMixture = (mixtureJson) => {
    const p = parseSubstrate(mixtureJson);
    if (!Array.isArray(p)) return;
    const availableNames = new Set(substrates.map(s => s.name));
    const valid = p.filter(c => availableNames.has(c.name));
    if (valid.length < 2) return;
    clearedPctNamesRef.current.clear();
    setSelection(Object.fromEntries(valid.map(c => [c.name, String(c.pct)])));
  };

  const totalPct = selectedNames.reduce((s, n) => s + (parseInt(selection[n]) || 0), 0);
  const remaining = 100 - totalPct;
  const showError = isMixture && totalPct !== 0 && totalPct !== 100;

  const availableNames = new Set(substrates.map(s => s.name));
  const validRecentMixtures = recentMixtures.filter(m => {
    const p = parseSubstrate(m.substrate);
    return Array.isArray(p) && p.length >= 2 && p.every(c => availableNames.has(c.name));
  });

  return (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <div className="substrate-chips">
        {substrates.map(s => (
          <button
            key={s.id}
            type="button"
            className={`substrate-chip${s.name in selection ? ' substrate-chip--selected' : ''}`}
            onClick={() => handleChipClick(s.name)}
          >
            {s.name}
          </button>
        ))}
      </div>

      {isMixture && (
        <div className="substrate-mixture-editor">
          {validRecentMixtures.length > 0 && (
            <div className="substrate-mixture-recent">
              <span className="substrate-mixture-recent-label">Recent:</span>
              {validRecentMixtures.map((m, i) => (
                <button
                  key={i}
                  type="button"
                  className="substrate-mixture-recent-pill"
                  onClick={() => applyRecentMixture(m.substrate)}
                >
                  {m.formatted}
                </button>
              ))}
            </div>
          )}

          <div className="substrate-mixture-components">
            {selectedNames.map(name => (
              <div key={name} className="substrate-mixture-row">
                <span className="substrate-mixture-name">{name}</span>
                <div className="substrate-mixture-pct-wrap">
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={selection[name]}
                    onChange={e => handlePctChange(name, e.target.value)}
                    onBlur={() => handlePctBlur(name)}
                    placeholder="—"
                    className="substrate-mixture-pct"
                  />
                  <span className="substrate-mixture-pct-symbol">%</span>
                </div>
              </div>
            ))}
          </div>

          <div className={`substrate-mixture-total${remaining === 0 ? ' substrate-mixture-total--valid' : remaining < 0 ? ' substrate-mixture-total--error' : ''}`}>
            {remaining === 0 ? '✓ 100%' : remaining > 0 ? `${remaining}% remaining` : `${Math.abs(remaining)}% over`}
          </div>

          {showError && (
            <div className="substrate-mixture-error">Percentages must total 100%.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── GrowingMethodSubstrateSelect ──────────────────────────────────────────────
// Cascading growing method + substrate picker. Uses SubstratePicker for the
// substrate half so mixture mode is available in all observation forms.

export function GrowingMethodSubstrateSelect({
  methods,
  methodValue,
  substrateValue,
  onMethodChange,
  onSubstrateChange,
  methodLabel = 'Growing Method',
  substrateLabel = 'Substrate',
}) {
  const substrates = methods.find(m => m.name === methodValue)?.substrates ?? [];

  return (
    <>
      <div className="form-field">
        <label className="form-label">{methodLabel}</label>
        <select
          className="form-input"
          value={methodValue}
          onChange={e => { onMethodChange(e.target.value); onSubstrateChange(''); }}
        >
          <option value="">— select —</option>
          {methods.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
      </div>
      {methodValue && (
        <SubstratePicker
          key={methodValue}
          substrates={substrates}
          value={substrateValue}
          onChange={onSubstrateChange}
          label={substrateLabel}
          methodName={methodValue}
        />
      )}
    </>
  );
}
